import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createApiClient } from '../client';
import { useAuth } from '../../auth/useAuth';

export interface EntityRelationshipDto {
  id: string;
  entityAId: string;
  entityBId: string;
  relationshipType: string;
  notes?: string | null;
  entityAName?: string | null;
  entityBName?: string | null;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

export function useEntityRelationships(entityId: string) {
  const { getToken } = useAuth();
  return useQuery<EntityRelationshipDto[]>({
    queryKey: ['entity-relationships', entityId],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const response = await client
        .get(`api/v2/entities/${entityId}/relationships`)
        .json<ApiEnvelope<EntityRelationshipDto[]>>();
      return response.data ?? [];
    },
  });
}

export function useAddEntityRelationship(entityId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      targetEntityId: string;
      relationshipType: string;
      notes?: string;
    }) => {
      const client = createApiClient(getToken);
      const response = await client
        .post(`api/v2/entities/${entityId}/relationships`, { json: input })
        .json<ApiEnvelope<EntityRelationshipDto>>();
      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['entity-relationships', entityId],
      });
    },
  });
}

export function useDeleteEntityRelationship(entityId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (relationshipId: string) => {
      const client = createApiClient(getToken);
      await client.delete(`api/v2/entity-relationships/${relationshipId}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['entity-relationships', entityId],
      });
    },
  });
}
